import { useEditor, useValue } from "@tldraw/editor";
import {
  DefaultQuickActions,
  DefaultStylePanel,
  DefaultStylePanelContent,
  TldrawUiMenuActionItem,
  TLUiStylePanelProps,
} from "tldraw";
import { useEffect, useRef, useState } from "react";
import { useNotepadStore } from "@/features/notepad/store/useNotepadStore";

const EXIT_DURATION_MS = 300;

// ─── QuickActions ─────────────────────────────────────────────────────────────
// undo / redo / delete / duplicate quick-action row. Used by the spaces canvas
// (features/spaces) as its `QuickActions` component; the notepad v2 canvas uses
// CanvasToolbar instead and does not render this.
export function CanvasMenuPanel() {
  const editor = useEditor();
  const canUndo = useValue("canUndo", () => editor.getCanUndo(), [editor]);
  const canRedo = useValue("canRedo", () => editor.getCanRedo(), [editor]);
  const hasSelection = useValue(
    "hasSelection",
    () => editor.getSelectedShapeIds().length > 0,
    [editor],
  );

  return (
    <DefaultQuickActions>
      <TldrawUiMenuActionItem actionId="undo" disabled={!canUndo} />
      <TldrawUiMenuActionItem actionId="redo" disabled={!canRedo} />
      <TldrawUiMenuActionItem actionId="delete" disabled={!hasSelection} />
      <TldrawUiMenuActionItem actionId="duplicate" disabled={!hasSelection} />
    </DefaultQuickActions>
  );
}

// ─── StylePanel ───────────────────────────────────────────────────────────────
// Default style panel vertically centered on the right side
export function CanvasStylePanel(props: TLUiStylePanelProps) {
  return (
    <div
      style={{
        position: "absolute",
        top: "50%",
        right: 10,
        transform: "translateY(-50%)",
        pointerEvents: "all",
      }}
    >
      <DefaultStylePanel {...props}>
        <DefaultStylePanelContent />
      </DefaultStylePanel>
    </div>
  );
}

// ─── BackToContent ────────────────────────────────────────────────────────────
// Rendered outside tldraw in SystemToasts, reads state from NotepadStore.
// Visibility tracking and action registration done by CanvasOutOfViewTracker (inside tldraw).
export function CanvasBackToContent() {
  const isOutOfView = useNotepadStore((s) => s.isContentOutOfView);
  const onBackToContent = useNotepadStore((s) => s.onBackToContent);
  const setContentOutOfView = useNotepadStore((s) => s.setContentOutOfView);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOutOfView) {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      setIsExiting(false);
      setIsVisible(true);
    } else if (isVisible) {
      setIsExiting(true);
      exitTimerRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsExiting(false);
      }, EXIT_DURATION_MS);
    }
  }, [isOutOfView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    },
    [],
  );

  if (!isVisible && !isExiting) return null;

  return (
    <div
      className="flex justify-center pointer-events-none w-full"
      style={{
        opacity: isExiting ? 0 : 1,
        transform: isExiting ? "translateY(-16px)" : "translateY(0)",
        transition: `opacity ${EXIT_DURATION_MS}ms ease-out, transform ${EXIT_DURATION_MS}ms ease-out`,
      }}
    >
      <div
        className="pointer-events-auto overflow-hidden rounded-[10px]"
        style={{ background: "#FFF0D1", minWidth: 340 }}
      >
        <div className="flex items-center gap-3 px-5 py-[15px]">
          <img
            src="/assets/warning_alt.svg"
            alt="Warning"
            style={{ width: 22, height: 22, flexShrink: 0 }}
          />
          <span
            style={{
              fontFamily: "'Mulish', sans-serif",
              fontSize: 15,
              fontWeight: 700,
              color: "#1a1a1a",
              flex: 1,
              whiteSpace: "nowrap",
            }}
          >
            Your content is out of view
          </span>
          <button
            onClick={() => {
              onBackToContent?.();
              setContentOutOfView(false);
            }}
            style={{
              fontFamily: "'Mulish', sans-serif",
              fontSize: 14,
              fontWeight: 700,
              color: "#1a1a1a",
              background: "#FFFFFF",
              border: "none",
              borderRadius: 8,
              padding: "7px 14px",
              cursor: "pointer",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            Show
          </button>
        </div>
      </div>
    </div>
  );
}
